import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule, listDeliveries, type PushDelivery } from '@/api/push';

export function PushScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const scheduleId = id ?? '';

  const [since, setSince] = useState('');

  // ─── Queries ──────────────────────────────────────────────────────────────────

  const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['push-schedule', scheduleId],
    queryFn: () => getSchedule(scheduleId),
    enabled: !!scheduleId,
  });
  const schedule = scheduleData?.schedule;

  const { data: deliveriesData, isLoading: deliveriesLoading } = useQuery({
    queryKey: ['push-deliveries', scheduleId, since],
    queryFn: () => listDeliveries(scheduleId, since),
    enabled: !!scheduleId,
  });
  const deliveries = deliveriesData?.deliveries ?? [];
  const counts = deliveriesData?.counts ?? { sent: 0, failed: 0, token_invalid: 0 };

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleSinceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val) {
      // Convert from datetime-local format (YYYY-MM-DDTHH:MM) to SQL format (YYYY-MM-DD HH:MM:SS)
      const [date, time] = val.split('T');
      setSince(`${date} ${time}:00`);
    } else {
      setSince('');
    }
  };

  const sinceDisplayValue = useMemo(() => {
    if (!since) return '';
    // Convert from SQL format (YYYY-MM-DD HH:MM:SS) back to datetime-local (YYYY-MM-DDTHH:MM)
    const [date, time] = since.split(' ');
    return `${date}T${time?.slice(0, 5) ?? ''}`;
  }, [since]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (scheduleLoading || !schedule) {
    return <div className="text-sm text-content-subtle">Loading…</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">
          Push · Schedule · {schedule.name}
        </h1>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-xs uppercase text-content-subtle">Sent</div>
          <div className="text-2xl font-semibold mt-1 text-success">{counts.sent.toLocaleString()}</div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-xs uppercase text-content-subtle">Failed</div>
          <div className="text-2xl font-semibold mt-1 text-danger">{counts.failed.toLocaleString()}</div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-xs uppercase text-content-subtle">Token invalid</div>
          <div className="text-2xl font-semibold mt-1 text-danger">{counts.token_invalid.toLocaleString()}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-content-muted mb-2">Since</label>
        <input
          type="datetime-local"
          value={sinceDisplayValue}
          onChange={handleSinceChange}
          className="block px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Deliveries table */}
      {deliveriesLoading ? (
        <div className="text-sm text-content-subtle">Loading…</div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-xs text-content-subtle uppercase">
              <tr>
                <th className="text-left px-4 py-3">Sent at</th>
                <th className="text-left px-4 py-3">Device ID</th>
                <th className="text-left px-4 py-3">Locale</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {deliveries.map((d: PushDelivery) => (
                <tr key={d.id}>
                  <td className="px-4 py-3 text-content-muted">{d.sent_at}</td>
                  <td className="px-4 py-3 font-mono text-xs text-content-muted">{d.device_id}</td>
                  <td className="px-4 py-3 text-content-muted">{d.locale_used}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium ${
                        d.status === 'sent' ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-content-muted text-xs">{d.error || '—'}</td>
                </tr>
              ))}
              {deliveries.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-content-subtle text-sm" colSpan={5}>
                    No deliveries.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
